// ** React Imports
import { useContext, useState, useEffect, useMemo, cloneElement } from "react";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// #####################################################

const columnsDefault = {
	phone: 3,
	tablet: 3,
	desktop: 3,
	giant: 3,
};

// #####################################################

/**
 * @param {{ phone: number, tablet: number, desktop: number, giant: number }} [columns] - Optional custom column configuration
 * @param {string} [customMaxWidth] - Optional max width for the container
 * @param {Object} [style] - Optional inline styles for the container
 * @param {React.ReactNode[]} children - Children elements, should be passed as an array (e.g., list of <MasonryImage> / <MasonryVideo> components)
 */
const Masonry = ({
	columns: columnsByProps,
	customMaxWidth,
	style,
	children,
}) => {
	const { isPhone, isTablet, isDesktop, isGiant } =
		useContext(deviceTypeContext);

	const columns = columnsByProps ? columnsByProps : columnsDefault;

	// ** Ustawienie stanu początkowego - ilość kolumn w zależności od urządzenia użytkownika
	const [currentColumns, setCurrentColumns] = useState(() => {
		if (isPhone) {
			return columns.phone;
		} else if (isTablet) {
			return columns.tablet;
		} else if (isDesktop && !isGiant) {
			return columns.desktop;
		} else {
			return columns.giant;
		}
	});

	const gap = isPhone ? "var(--row-gap-mobile)" : "var(--row-gap)";
	const gapHorizontal = isPhone
		? "var(--column-gap-mobile)"
		: "var(--column-gap)";

	const finalArray = useMemo(() => {
		const divideChildrenIntoArrays = (children, numColumns) => {
			if (!children || children.length === 0) return [];
			if (numColumns <= 0) numColumns = 1;

			// Original width of the image before rendering is 210px.
			const BASE_WIDTH = 210;
			const MAX_ASPECT_RATIO = 4 / 3;
			const childrenCount = children.length;

			// Using fill() because Array constructor creates empty slots (not undefined values) which map() skips
			// Creating new array instance for each column to avoid shared references
			// Not using Array.from for better browser compatibility
			const columns = Array(numColumns)
				.fill()
				.map(() => []);
			const columnHeights = Array(numColumns).fill(0);

			for (let i = 0; i < childrenCount; i++) {
				const child = children[i];

				const height =
					child?.props?.height || child?.props?.contentHeight || 1;
				const width =
					child?.props?.width || child?.props?.contentWidth || 1;
				let effectiveWidth = width;
				let effectiveHeight = height;
				const aspectRatio = width / height;

				if (aspectRatio > MAX_ASPECT_RATIO) {
					effectiveWidth = MAX_ASPECT_RATIO * effectiveHeight;
				} else if (1 / aspectRatio > MAX_ASPECT_RATIO) {
					effectiveHeight = MAX_ASPECT_RATIO * effectiveWidth;
				}

				// +75 is masonry bottom and top bar height
				const displayHeight = Math.round(
					(effectiveHeight / effectiveWidth) * BASE_WIDTH + 75,
				);

				let minColumnIndex = 0;
				for (let j = 1; j < numColumns; j++) {
					if (columnHeights[j] < columnHeights[minColumnIndex]) {
						minColumnIndex = j;
					}
				}

				columnHeights[minColumnIndex] += displayHeight;
				columns[minColumnIndex].push(
					// cloneElement - Prevent Kibana Error: The node to be removed is not a child of this node.
					cloneElement(child, {
						correctHeight: displayHeight,
					}),
				);
			}

			return columns;
		};

		if (children && children.length > 0) {
			return divideChildrenIntoArrays(children, currentColumns);
		}
	}, [children, currentColumns]);

	useEffect(() => {
		if (isPhone) {
			setCurrentColumns(columns.phone);
		} else if (isTablet) {
			setCurrentColumns(columns.tablet);
		} else if (isDesktop && !isGiant) {
			setCurrentColumns(columns.desktop);
		} else if (isDesktop && isGiant) {
			setCurrentColumns(columns.giant);
		}
	}, [isPhone, isTablet, isDesktop, isGiant]);

	// #####################################################

	return (
		<div
			style={{
				display: "flex",
				width: "100%",
				maxWidth: customMaxWidth || "100%",
				...style,
			}}
		>
			{finalArray?.map((column, columnIndex) => {
				return (
					<div
						key={columnIndex}
						style={{
							marginLeft: columnIndex && gap,
							width: "100%",
							maxWidth: "100%",
							minWidth: customMaxWidth ? 0 : "unset",
						}}
					>
						{column.map((item, itemIndex) => {
							return (
								<div
									// item.key - Prevent Kibana Error: The node to be removed is not a child of this node.
									key={item.key || itemIndex}
									style={{
										paddingTop: itemIndex && gapHorizontal,
										width: "100%",
										maxWidth: "100%",
									}}
								>
									{item}
								</div>
							);
						})}
					</div>
				);
			})}
		</div>
	);
};

// #####################################################

export default Masonry;
